

import { useState, useRef } from "react";
import API_CONFIG from "../../../Configuration/apiConfig";

export function useImages() {
  const presignedUrlCache = useRef({});

  const [modalActualImages, setModalActualImages] = useState([]);
  const [modalAnnotatedImages, setModalAnnotatedImages] = useState([]);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [actualImageUrl, setActualImageUrl] = useState("");
  const [annotatedImageUrl, setAnnotatedImageUrl] = useState("");
  const [selectedStoreImages, setSelectedStoreImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageRotation, setImageRotation] = useState(0);

  const [activationImageUrl, setActivationImageUrl] = useState("");
  const [isActivationImageOpen, setIsActivationImageOpen] = useState(false);
  const [loadingActivationImage, setLoadingActivationImage] = useState(false);
  const [coolerImageUrl, setCoolerImageUrl] = useState("");
  const [isCoolerImageOpen, setIsCoolerImageOpen] = useState(false);
  const [loadingCoolerImage, setLoadingCoolerImage] = useState(false);

 
  const generatePresignedUrls = async (s3Paths, month, year) => {
    const presignedPromises = s3Paths.map(async (s3Path) => {
      const cacheKey = `${s3Path}_${month}_${year}`;
      const cached=presignedUrlCache.current[cacheKey]
      if (cached && Date.now()<cached.expiresAt) {
        return cached.url;
      }
      const presignedEndpoint = `${API_CONFIG.ENDPOINTS.STORE_REPORT.S3_PRESIGNED_URL}?fileName=${encodeURIComponent(
        s3Path
      )}&bucketName=imageinsightimagesbeverages&operation=download&month=${month}&year=${year}&ResponseContentDisposition=inline`;
    
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        const response = await fetch(presignedEndpoint, { signal: controller.signal });
        clearTimeout(timeout);
        if (!response.ok) throw new Error("`Failed to fetch presigned URL");
        const presignedUrl = await response.text();
        const trimmed = presignedUrl.trim();
        presignedUrlCache.current[cacheKey] ={url: trimmed,
          expiresAt:Date.now()+50*60*1000};
        return trimmed;
      } catch (err) {
        console.error("Failed to generate presigned URL:", err);
        return null;
      }
    });
    return (await Promise.all(presignedPromises)).filter(Boolean);
  };


  const generateDownloadUrl = async (s3Path, month, year) => {
    const presignedEndpoint = `${API_CONFIG.ENDPOINTS.STORE_REPORT.S3_PRESIGNED_URL}?fileName=${encodeURIComponent(
      s3Path
    )}&bucketName=imageinsightimagesbeverages&operation=download&month=${month}&year=${year}&ResponseContentDisposition=attachment`;
    try {
      const response = await fetch(presignedEndpoint);
      if (!response.ok) throw new Error("Failed to fetch download URL");
      return (await response.text()).trim();
    } catch (err) {
      console.error("Failed to generate download URL:", err);
      return null;
    }
  };

  const clearCache = () => {
    presignedUrlCache.current = {};
  };

  const resetImages = () => {
    setModalActualImages([]);
    setModalAnnotatedImages([]);
    setModalImageIndex(0);
    setActualImageUrl("");
    setAnnotatedImageUrl("");
    setSelectedStoreImages([]);
    setCurrentImageIndex(0);
    setImageRotation(0);
    setActivationImageUrl("");
    setCoolerImageUrl("");
  };

  const handlePrevModalImage = () => {
    if (modalImageIndex > 0) {
      const newIndex = modalImageIndex - 1;
      setModalImageIndex(newIndex);
      setActualImageUrl(modalActualImages[newIndex] || "");
      setAnnotatedImageUrl(modalAnnotatedImages[newIndex] || "");

      const prefetchIndex = newIndex - 1;
      [modalActualImages[prefetchIndex], modalAnnotatedImages[prefetchIndex]]
        .filter(Boolean)
        .forEach(src => { new window.Image().src = src; });
    }
  };

  const handleNextModalImage = () => {
    if (modalImageIndex < modalActualImages.length - 1) {
      const newIndex = modalImageIndex + 1;
      setModalImageIndex(newIndex);
      setActualImageUrl(modalActualImages[newIndex] || "");
      setAnnotatedImageUrl(modalAnnotatedImages[newIndex] || "");

  
      const prefetchIndex = newIndex + 1;
      [modalActualImages[prefetchIndex], modalAnnotatedImages[prefetchIndex]]
        .filter(Boolean)
        .forEach(src => { new window.Image().src = src; });
    }
  };

  
  const handleDownloadImage = async (imageUrl, s3Path, month, year) => {
    if (!imageUrl) return;
    try {
   
      const downloadUrl = (s3Path && month && year)
        ? await generateDownloadUrl(s3Path, month, year)
        : imageUrl;

      const response = await fetch(downloadUrl || imageUrl, { mode: "cors", cache: "no-cache" });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "image.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.warn("Direct download failed, falling back to new tab:", error);
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = "image.jpg";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleViewActivation = async (detail, toDate) => {
    try {
      setLoadingActivationImage(true);
      const s3Path = detail?.s3ActualPath;
      if (!s3Path) return;
      const [toYear, toMonthNum] = toDate.split("-").map(Number);
      const urls = await generatePresignedUrls([s3Path], toMonthNum, toYear);
      setActivationImageUrl(urls[0] || "");
      setIsActivationImageOpen(true);
    } catch (err) {
      console.error("Failed to load activation image:", err);
    } finally {
      setLoadingActivationImage(false);
    }
  };

  const handleViewCoolerImage = async (s3Path, toDate) => {
  try {
    setLoadingCoolerImage(true);
    if (!s3Path) return;
    const [toYear, toMonthNum] = toDate.split("-").map(Number);
    const urls = await generatePresignedUrls([s3Path], toMonthNum, toYear);
    setCoolerImageUrl(urls[0] || "");
    setIsCoolerImageOpen(true);
  } catch (err) {
    console.error("Failed to load cooler image:", err);
  } finally {
    setLoadingCoolerImage(false);
  }
};

  return {
    // Availability images
    modalActualImages, setModalActualImages,
    modalAnnotatedImages, setModalAnnotatedImages,
    modalImageIndex, setModalImageIndex,
    actualImageUrl, setActualImageUrl,
    annotatedImageUrl, setAnnotatedImageUrl,
    // Store images carousel
    selectedStoreImages, setSelectedStoreImages,
    currentImageIndex, setCurrentImageIndex,
    imageRotation, setImageRotation,
    // Activation image
    activationImageUrl, setActivationImageUrl,
    isActivationImageOpen, setIsActivationImageOpen,
    loadingActivationImage,
    // Actions
    coolerImageUrl, setCoolerImageUrl,
    isCoolerImageOpen, setIsCoolerImageOpen,
    loadingCoolerImage,

    generatePresignedUrls,
    generateDownloadUrl,
    clearCache,
    resetImages,
    handlePrevModalImage,
    handleNextModalImage,
    handleDownloadImage,
    handleViewActivation,
    handleViewCoolerImage,
  };
}
